// Kustomize Components page (roadmap item 43).
//
// Mounted at `/repos/:id/kustomize` for any repository whose `format` is
// `kustomize`. Shows the catalog of (component, tag) pairs the repo holds
// and, for the component the operator clicks, the per-layer breakdown
// plus a kustomization.yaml preview — that's the part of a Kustomize
// component that matters for review before promoting it to a base.
//
// Slug derivation matches the OllamaModels page: `useRepoDetail` is
// UUID-keyed, the OCI surface is slug-keyed, so we look up the project
// slug from the cached project list.

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Boxes,
  Copy,
  FileCode,
  FileText,
  Layers,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useProjects, useRepoDetail } from '../../api/client';
import {
  type KustomizeComponentEntry,
  type KustomizeLayer,
  useKustomizationPreview,
  useKustomizeComponentDetail,
  useKustomizeComponents,
} from '../../api/kustomize';

function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function shortHex(d?: string): string {
  if (!d) return '';
  if (d.length <= 19) return d;
  return d.slice(0, 12) + '…' + d.slice(-4);
}

// Kustomize-specific layer media types, mapped to the human label and the
// icon the catalog UI renders.
const LAYER_KIND_BY_MEDIA: Record<string, { label: string; icon: typeof Boxes }> = {
  'application/vnd.cncf.kustomize.component.v1.tar+gzip': {
    label: 'Component bundle',
    icon: Boxes,
  },
  'application/vnd.cncf.kustomize.component.config.v1+json': {
    label: 'Config',
    icon: FileText,
  },
};

function layerKindLabel(mediaType: string): { label: string; icon: typeof Boxes } {
  return LAYER_KIND_BY_MEDIA[mediaType] ?? { label: 'Layer', icon: Layers };
}

function componentsCountLabel(comps: { components: unknown[] } | undefined): string | undefined {
  if (!comps) return undefined;
  return `${comps.components.length} component${comps.components.length === 1 ? '' : 's'}`;
}

export function KustomizeComponents() {
  const { id } = useParams<{ id: string }>();
  const { data: repo, isLoading: repoLoading, error: repoError } = useRepoDetail(id);
  const { data: projects } = useProjects();
  const projectSlug = useMemo(
    () => projects?.find((p) => p.id === repo?.projectId)?.slug,
    [projects, repo?.projectId],
  );
  const { data: comps, error: compsError, isLoading: compsLoading } = useKustomizeComponents(
    projectSlug,
    repo?.slug,
  );
  const [selected, setSelected] = useState<KustomizeComponentEntry | null>(null);
  const detail = useKustomizeComponentDetail(
    projectSlug,
    repo?.slug,
    selected?.component,
    selected?.tag,
  );

  if (repoLoading) {
    return <div className="p-6 text-fg-muted">Loading repository…</div>;
  }
  if (repoError || !repo) {
    return (
      <div className="p-6 text-danger">
        Failed to load repository: {String(repoError ?? 'unknown')}
      </div>
    );
  }
  if (repo.format !== 'kustomize') {
    return (
      <div className="p-6">
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to repository
        </Link>
        <p className="text-fg-muted">
          This page is only available for repositories of format{' '}
          <code className="orb-code">kustomize</code>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/repos/${repo.id}`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {repo.name}
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">Kustomize components</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
          {repo.name}
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">
          OCI-distributed Kustomize Components in this repository. Each row
          shows the tag, manifest digest, and total on-disk size of the
          referenced layers. Click a row for the per-layer breakdown plus
          the kustomization.yaml preview.
        </p>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Boxes className="h-4 w-4 text-accent" />
              Catalog
            </span>
          }
          description={componentsCountLabel(comps)}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">Component</th>
                <th className="px-4 py-2 font-medium">Tag</th>
                <th className="px-4 py-2 font-medium">Layers</th>
                <th className="px-4 py-2 font-medium">Total size</th>
                <th className="px-4 py-2 font-medium">Manifest digest</th>
                <th className="px-4 py-2 font-medium">Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {compsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    Loading…
                  </td>
                </tr>
              )}
              {compsError && (
                <tr>
                  <td className="px-4 py-3 text-danger" colSpan={6}>
                    {String(compsError)}
                  </td>
                </tr>
              )}
              {comps?.components.length === 0 && !compsLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    No components pushed yet. Use{' '}
                    <code className="orb-code">
                      kustomize push oci://{'<host>'}/kustomize/{projectSlug}/{repo.slug}/{'<component>'}:{'<tag>'}
                    </code>{' '}
                    to seed the repo.
                  </td>
                </tr>
              )}
              {comps?.components.map((c) => {
                const isSelected =
                  selected?.component === c.component && selected?.tag === c.tag;
                return (
                  <tr
                    key={`${c.component}:${c.tag}`}
                    className={`border-b border-border/50 cursor-pointer ${
                      isSelected ? 'bg-bg-hover/60' : 'hover:bg-bg-hover/40'
                    }`}
                    onClick={() => setSelected(c)}
                  >
                    <td className="px-4 py-2 font-medium text-fg">{c.component}</td>
                    <td className="px-4 py-2">
                      <Badge tone="accent">{c.tag}</Badge>
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {c.layer_count}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-fg-muted">
                      {bytes(c.total_size_bytes)}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-fg-muted">
                      {shortHex(c.digest)}
                    </td>
                    <td className="px-4 py-2 text-fg-muted">
                      {new Date(c.uploaded_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {comps && (
        <Card>
          <CardHeader title="How to pull" />
          <CardBody>
            <PullSnippet hint={comps.pull_hint} />
          </CardBody>
        </Card>
      )}

      {selected && (
        <>
          <Card>
            <CardHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <Layers className="h-4 w-4 text-accent" />
                  {selected.component}
                  <Badge tone="accent">{selected.tag}</Badge>
                </span>
              }
              description="Manifest layers — bundle layer carries the kustomization.yaml + referenced files."
            />
            <CardBody>
              {detail.isLoading && <p className="text-fg-muted">Loading detail…</p>}
              {detail.error && (
                <p className="text-danger">{String(detail.error)}</p>
              )}
              {detail.data && <ComponentDetail detail={detail.data} />}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <FileCode className="h-4 w-4 text-accent" />
                  kustomization.yaml
                </span>
              }
              description="First kustomization.yaml found inside the bundle layer."
            />
            <CardBody>
              <KustomizationPreview
                projectSlug={projectSlug}
                repoSlug={repo.slug}
                component={selected.component}
                tag={selected.tag}
              />
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}

interface PullSnippetProps {
  readonly hint: string;
}

function PullSnippet({ hint }: PullSnippetProps) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(hint);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail in non-secure contexts; the operator can
      // always select the snippet manually so we don't surface an error.
    }
  };
  return (
    <div className="flex items-start gap-3">
      <pre className="flex-1 bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre">
        {hint}
      </pre>
      <Button variant="ghost" type="button" onClick={onCopy} title="Copy snippet">
        <Copy className="h-4 w-4" />
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

interface ComponentDetailProps {
  readonly detail: {
    component: string;
    tag: string;
    digest: string;
    size_bytes: number;
    uploaded_at: string;
    metadata: Record<string, unknown>;
    pull_hint: string;
  };
}

function ComponentDetail({ detail }: ComponentDetailProps) {
  const meta = detail.metadata ?? {};
  const layers = (meta.layers as KustomizeLayer[] | undefined) ?? [];
  const total = (meta.total_size as number | undefined) ?? 0;
  const configDigest = meta.config_digest as string | undefined;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">
            Manifest digest
          </dt>
          <dd className="font-mono text-xs text-fg break-all">{detail.digest}</dd>
        </div>
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">Total size</dt>
          <dd className="tabular-nums text-fg">{bytes(total || detail.size_bytes)}</dd>
        </div>
        {configDigest && (
          <div>
            <dt className="text-fg-muted text-xs uppercase tracking-wide">
              Config digest
            </dt>
            <dd className="font-mono text-xs text-fg break-all">{configDigest}</dd>
          </div>
        )}
        <div>
          <dt className="text-fg-muted text-xs uppercase tracking-wide">Uploaded</dt>
          <dd className="text-fg-muted">
            {new Date(detail.uploaded_at).toLocaleString()}
          </dd>
        </div>
      </dl>

      <div>
        <h3 className="text-sm font-semibold text-fg mb-2">
          Layers ({layers.length})
        </h3>
        {layers.length === 0 ? (
          <p className="text-fg-muted text-sm">
            Manifest carries no parsed layers — likely an unrecognised media
            type or a malformed manifest.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted border-b border-border">
                  <th className="px-3 py-2 font-medium">Kind</th>
                  <th className="px-3 py-2 font-medium">Media type</th>
                  <th className="px-3 py-2 font-medium">Digest</th>
                  <th className="px-3 py-2 font-medium">Size</th>
                </tr>
              </thead>
              <tbody>
                {layers.map((l) => {
                  const { label, icon: Icon } = layerKindLabel(l.media_type);
                  return (
                    <tr key={l.digest} className="border-b border-border/50">
                      <td className="px-3 py-2 text-fg">
                        <span className="inline-flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-accent" />
                          {label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-fg-muted">
                        {l.media_type}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-fg-muted">
                        {shortHex(l.digest)}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-fg-muted">
                        {bytes(l.size)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PullSnippet hint={detail.pull_hint} />
    </div>
  );
}

interface KustomizationPreviewProps {
  readonly projectSlug?: string;
  readonly repoSlug?: string;
  readonly component: string;
  readonly tag: string;
}

function KustomizationPreview({
  projectSlug,
  repoSlug,
  component,
  tag,
}: KustomizationPreviewProps) {
  const { data, error, isLoading } = useKustomizationPreview(
    projectSlug,
    repoSlug,
    component,
    tag,
  );
  if (isLoading) return <p className="text-fg-muted">Loading preview…</p>;
  if (error) {
    return (
      <p className="text-fg-muted text-sm">
        Preview unavailable — the bundle layer may not contain a
        kustomization.yaml at the root, or the manifest peek didn't capture
        a bundle digest. ({String(error)})
      </p>
    );
  }
  if (!data) return null;
  return (
    <pre className="bg-bg-inset border border-border rounded-md p-3 text-xs font-mono text-fg overflow-x-auto whitespace-pre max-h-[480px]">
      {data}
    </pre>
  );
}
